import React, { useEffect, useState } from 'react';
import {
  Trophy,
  Activity,
  BarChart3,
  Users,
  X,
} from 'lucide-react';
import { APP_STORE_URL, PLAY_STORE_URL } from './home/AppDownloadCta';

const APP_BANNER_DISMISSED_KEY = 'dc-app-banner-dismissed';
// Kurzer Delay NACH der ersten User-Interaktion, damit das Popup nicht
// mitten in eine Geste platzt
const SHOW_DELAY_MS = 1000;
// Erst nach ECHTER User-Interaktion zeigen: Lighthouse/PageSpeed interagiert
// nicht → das Popup zählt dort weder in LCP noch in CLS.
const INTERACTION_EVENTS = ['scroll', 'pointerdown', 'touchstart'] as const;

const FEATURES = [
  { icon: Trophy, text: 'Turniere finden & direkt anmelden' },
  { icon: Activity, text: 'Live-Scores der großen PDC-Turniere' },
  { icon: BarChart3, text: 'Statistiken & Rankings' },
  { icon: Users, text: 'Community, Forum & Tippspiele' },
];

// Offizielle Store-Logos als Inline-SVG (Simple Icons, currentColor →
// übernehmen automatisch die Textfarbe des jeweiligen Buttons)
export const AppleLogo: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className="-mt-0.5"
  >
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.03 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

export const GooglePlayLogo: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
  </svg>
);

/**
 * App-Download-Hinweis als Popup: Desktop zentriertes Modal, Mobile Vollbild.
 * Erscheint einmalig; „Weiter im Browser"/Schließen merkt sich die Entscheidung
 * dauerhaft (gleicher localStorage-Key wie der frühere Header-Banner).
 */
const AppDownloadPopup: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(APP_BANNER_DISMISSED_KEY) === 'true') return;
    } catch {
      // localStorage nicht verfügbar -> Popup trotzdem zeigen
    }
    let timer: number | undefined;
    const onFirstInteraction = () => {
      INTERACTION_EVENTS.forEach((event) =>
        window.removeEventListener(event, onFirstInteraction),
      );
      timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };
    INTERACTION_EVENTS.forEach((event) =>
      window.addEventListener(event, onFirstInteraction, { passive: true }),
    );
    return () => {
      INTERACTION_EVENTS.forEach((event) =>
        window.removeEventListener(event, onFirstInteraction),
      );
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(APP_BANNER_DISMISSED_KEY, 'true');
    } catch {
      // ignore storage errors
    }
  };

  if (!visible) return null;

  const isApple = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
  const primaryStoreUrl = isApple ? APP_STORE_URL : PLAY_STORE_URL;
  const secondaryStoreUrl = isApple ? PLAY_STORE_URL : APP_STORE_URL;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-zinc-950 sm:items-center sm:justify-center sm:bg-black/70 sm:p-4 sm:backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="DartCloud App herunterladen"
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-8 pt-16 sm:min-h-fit sm:flex-none sm:w-full sm:max-w-md sm:rounded-2xl sm:border sm:border-zinc-800 sm:bg-zinc-900 sm:px-8 sm:pb-8 sm:pt-10">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hinweis schließen"
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:bg-zinc-800 hover:text-white"
        >
          <X size={20} />
        </button>

        {/* Auf Mobile füllt der Inhaltsblock den Raum über den Buttons und
            zentriert sich darin — statt oben zu kleben */}
        <div className="flex flex-1 flex-col justify-center sm:flex-none">
          <div className="flex flex-col items-center text-center">
            <img
              src="/icon.png"
              alt=""
              className="h-20 w-20 rounded-2xl shadow-[0_8px_32px_rgba(63,206,209,0.25)]"
            />
            <h2 className="mt-6 text-2xl font-extrabold tracking-tight text-white">
              Hol dir die DartCloud App
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Alle Features – immer dabei auf deinem Smartphone.
            </p>
          </div>

          <ul className="mt-8 space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#3fced1]/20 bg-[#3fced1]/10">
                  <Icon size={17} className="text-[#3fced1]" />
                </span>
                <span className="text-sm text-gray-300">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-8 sm:mt-8 sm:pt-0">
          <a
            href={primaryStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-xl bg-[#3fced1] px-4 text-base font-semibold text-black hover:bg-[#35b5b8]"
          >
            {isApple ? <AppleLogo /> : <GooglePlayLogo />}
            {isApple ? 'Im App Store laden' : 'Bei Google Play laden'}
          </a>
          <a
            href={secondaryStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2.5 rounded-xl border border-zinc-700 px-4 text-sm font-semibold text-white hover:border-[#3fced1]/50"
          >
            {isApple ? <GooglePlayLogo size={16} /> : <AppleLogo size={16} />}
            {isApple ? 'Bei Google Play laden' : 'Im App Store laden'}
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="mt-4 w-full text-center text-sm font-medium text-gray-500 hover:text-gray-300"
          >
            Weiter im Browser
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDownloadPopup;
